import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import axios from "axios";

import { AuthProvider } from "./contexts/AuthContext";
import { RegionProvider } from "./contexts/RegionContext";
import Layout from "./components/layout";
import RoleBasedAccessControl from "./components/RoleBasedAccessControl";
import Home from "./screens/home";
import Users from "./screens/users";
import User from "./screens/user";
import CampTransactions from "./screens/campTransactions";
import LedgerActivity from "./screens/ledgerActivity";
import LedgerBalances from "./screens/ledgerBalances";
import JournalEntries from "./screens/journalEntries";
import { useEffect, useRef } from "react";
import { Hub } from "aws-amplify";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry 403 or 404 errors
        if (
          axios.isAxiosError(error) &&
          [403, 404].includes(error.response?.status || 0)
        ) {
          return false;
        }
        // Retry other errors up to 3 times
        return failureCount < 3;
      },
    },
  },
});

const App = () => {
  const redirectBasedOnRole = useRef(false);

  // Set redirectBasedOnRole flag by listening to Amplify's signIn event
  useEffect(() => {
    const unsub = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signIn" || payload.event === "signedIn") {
        redirectBasedOnRole.current = true;
      }
    });
    return () => unsub();
  }, []);

  return (
    <Authenticator hideSignUp={true}>
      {({ signOut }) => (
        <AuthProvider>
          <RegionProvider>
            <QueryClientProvider client={queryClient}>
              <BrowserRouter>
                <RoleBasedAccessControl
                  redirectBasedOnRole={redirectBasedOnRole}
                >
                  <Routes>
                    <Route path="/" element={<Layout signOut={signOut} />}>
                      <Route index element={<Home />} />
                      <Route path="users">
                        <Route index element={<Users />} />
                        <Route path=":userId">
                          <Route index element={<User />} />
                        </Route>
                      </Route>
                      <Route
                        path="camp-transactions"
                        element={<CampTransactions />}
                      />
                      <Route
                        path="ledger-activity"
                        element={<LedgerActivity />}
                      />
                      <Route
                        path="ledger-balances"
                        element={<LedgerBalances />}
                      />
                      <Route
                        path="journal-entries"
                        element={<JournalEntries />}
                      />
                    </Route>
                  </Routes>
                </RoleBasedAccessControl>
              </BrowserRouter>
            </QueryClientProvider>
          </RegionProvider>
        </AuthProvider>
      )}
    </Authenticator>
  );
};

export default App;
